/* eslint-disable prefer-destructuring */
/* eslint-disable func-names */

import { all, takeEvery, takeLatest, put, select, debounce, call, take } from 'redux-saga/effects';
import { resetRequests, success } from '@redux-requests/core';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import {
  startFileCreation,
  fetchFile,
  fetchFileList,
  startInspection,
  resetInspection,
  selectUnconfirmedZones,
  fetchLinkedInspections,
} from 'store/requests';
import {
  files,
  inspection,
  app,
  getUploadedFilesSelector,
  getInspectionId,
  getAutosaving,
  getGroupById,
  getUpdateInspectionData,
  getName,
  getPreviousDifferenceId,
  getFocusedDifference,
  getCreateInspectionData,
  getGroupedDifferences,
  getDifferenceGroupMembers,
  clearGroupedDifferencesSelectorCache,
  getDocumentsLoaded,
  getInspectionSettings,
  getFocusedDifferenceId,
  getSegmentDisplayedDifferenceCounts,
  getFilesIds,
  getDifferences,
  getSmartDiscardQty,
  getDisplayedAndHiddenDifferences,
  getSourceLayers,
  getTargetLayers,
  getSourceSeparations,
  getTargetSeparations,
  getSelectedModule,
  getStoredDifferenceSortOption,
  getSubgroupedDifferences,
  getUnfilteredDifferences,
  getDifferenceSortOption,
} from 'store';
import {
  CTFModuleTypes,
  SortDifferencesOption,
  DocumentType,
  DocumentTypes,
  Difference,
  DifferenceTypeNames,
  FileStatus,
  UpdateInspectionData,
  InspectFileState,
  UserPreferences,
  ToastStatuses,
  FilesIdData,
  Match,
  FileMetadata,
  PolygonZoneCustomData,
} from 'types';
import PDFTronAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import { applyGrayscaleMode } from 'pdftron/docManager/applyGrayscaleMode';
import docManagerSagas from 'pdftron/docManager/saga';
import { tableEnhancementFileCheck } from 'utils/fileTypeCheck/fileTypeCheck';
import { Core } from '@pdftron/webviewer';
import { excludePagesFromDocuments } from 'pdftron/docManager/excludePages';
import { isReportsUrl, isResultUrl } from 'utils/location';
import toastEvents from 'components/DocumentToast/toastEvents';
import { cleanupResyncZonesAndResetTool } from 'pdftron/docManager/AnnotationTools/ResyncZone';
import { getHideDisplayPanelsForModule } from 'store/app/appSelectors';

import {
  DifferenceViewOptionsPayload,
  Group,
  InputAnnotation,
  InspectionSettings,
  PDFTool,
  PDFTRON_DEFAULT_TOOL,
  PDFTronTools,
  Layer,
  Separation,
} from 'types/inspection';
import Utils from 'components/PDFViewer/Utils';
import { normalizeAnnotationRect } from 'pdftron/docManager/Difference';
import report from './report/reducer';
import reportSagas from './report/sagas';
import differencesSagas from './request/differences/sagas';

import {
  getFocusedMatchIndex,
  getGrayscaleMode,
  getHideOtherDifferences,
  getShowLiveText,
  getSnackMessage,
  getToastStatuses,
  getGraphicMode,
  getUpdatingInspection,
  getUserPreferences,
} from './app/appSelectors';
import { updateUserData } from './myAccount/requests';
import { trackTrackerEvent } from '../components/Tracker/TrackerProvider';
import auth0Sagas from './auth/auth0Saga';
import appSagas from './app/appSagas';
import displaySettingsSagas from './app/restoreDisplaySettingsInStore';
import { fetchDifferences, updateDifference } from './request/differences/actions';
import { fetchGroups, patchGroup } from './request/difference-group/actions';
import { useDiffZoomStore } from 'zstore/diffZoomStore';
import { useDifferenceListStore, rebuildDifferenceList } from 'zstore/differenceListStore';
import {
  FileErrors,
  FileFlags,
  type FileMergeMetadata as MergedFileData,
  type SubgroupedDifferences,
} from '@verify/shared-utils';
import { queryClient } from 'queryClient';
import type { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import {
  getLiveTextNotFound,
  getExcludedPage,
  getAnnotations,
  getSpellingTrackingData,
  getNumberOfAnnotations,
  getAllPageLoadedSelector,
  getIsSingleFile,
  getLastJobMatches,
  getDisplayedAndHiddenRepeats,
  getSelectedTool,
} from './inspection/inspectionSelectors';
import {
  runInspection,
  createInspection,
  updateInspection,
  updateInspectionFiles,
  fetchInspection,
  pollInspectionStatus,
  CreateInspectionData,
} from './request/inspections/actions';
import { GVSnackMessage } from 'components/lib/GVSnackbar/GVSnackbar';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { CtfMatchResult, CtfMatchResultWithDifferences } from './request/match/types';
import store from './store';
import { v4 as uuidv4 } from 'uuid';
import * as fileSelectors from 'store/files/filesSelectors';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { setClientTimezoneOffset } from './trackerMetadata/trackerMetadata';
import { FlashAnimationManager } from 'pdftron/docManager/AnnotationTools/utils/FlashAnimationManager';

export interface FileData {
  id: string;
  filename: string;
  originalFilename: string;
  userId: string;
  path?: string;
  signedUrl: string;
  status: FileStatus;
  createdAt: string;
  error?: FileErrors | null;
  mergedMetadata?: MergedFileData[];
  isOCR?: boolean;
  flags?: FileFlags[];
  originalFileId?: string;
  fileMetadata: FileMetadata;
}

interface FileUploadRequest extends Action {
  payload: {
    request: {
      data: {
        fileName: string;
        removeHeadersFooters?: boolean;
      };
    };
  };
  meta: {
    document: DocumentType;
  };
}

interface BatchFileUploadRequest extends Action {
  meta: {
    fileCount: number;
    document: DocumentType;
    fileName: string;
    removeHeadersFooters?: boolean;
  };
}

interface FileListResult extends Action {
  payload: {
    data: Array<FileData>;
  };
}

interface ExcludePagePayload extends Action {
  payload: {
    documentType: DocumentType;
    pageNumber: number;
  };
}

interface ExcludePagesPayload extends Action {
  payload: {
    documentType: DocumentType;
    pageNumbers: number[];
  };
}

interface FetchFilePayload extends Action {
  payload: {
    data: FileData;
  };
  meta: {
    document: DocumentType;
  };
}

function* handleFilesList(action: FileListResult) {
  const { data } = action.payload;

  const filesList = data.map((f) => ({
    fileId: f.id,
    url: f.signedUrl,
    status: f.status,
    fileName: f.filename,
    originalName: f.originalFilename,
    createdAt: f.createdAt,
    mergedFileData: f.mergedMetadata,
    isOCR: f.flags?.includes(FileFlags.ocr),
    fileMetadata: f.fileMetadata,
  }));

  yield put(files.actions.setFilesList(filesList));
}

function* saveInspection() {
  const id: string = yield select(getInspectionId);
  const updateData: UpdateInspectionData = yield select(getUpdateInspectionData);
  yield put(updateInspection(id, updateData));
  if (updateData.settings?.singleFile) {
    PDFTronManager.setPDFInstance(DocumentTypes.source, null);
  }

  // Status is now updated in updateInspection action's onSuccess based on backend response
  // This allows imported inspections to stay Complete on first PDF metadata update
}

function* autoSaveInspection() {
  const hasLoaded: boolean = yield select(getAllPageLoadedSelector);
  const id: string = yield select(getInspectionId); // prevent saving while fetching the new inspection
  if (id && hasLoaded) {
    yield call(saveInspection);
  } else {
    yield put(app.actions.setAutosaving(false));
  }
}

// We need a separate function for updating the name because we won't change the inspection status.
function* autoSaveName(): any {
  const hasLoaded = (yield select(getAllPageLoadedSelector)) as boolean;
  const id = (yield select(getInspectionId)) as string; // prevent saving while fetching the new inspection
  const name = (yield select(getName)) as string;
  if (id && hasLoaded) {
    yield put(updateInspection(id, { name }));

    // Refetch linked inspections to update name in dropdown
    const state = (yield select((state: any) => state)) as any;
    const groupId = state.inspection.settings?.groupId;
    if (groupId) {
      yield put(fetchLinkedInspections(groupId));
    }
  } else {
    yield put(app.actions.setAutosaving(false));
  }
}

function* handleSetAutosavingFlag() {
  yield put(app.actions.setAutosaving(true));
}

function* handleSetDifferenceViewOptions(action: PayloadAction<DifferenceViewOptionsPayload | CTFModuleTypes | 'All'>) {
  // payload is a string when triggered by setSelectedModule (module switch), an object for setDifferenceViewOptions (filters/sort)
  const forceVisibleDifferenceId =
    typeof action.payload === 'string' ? undefined : action.payload.forceVisibleDifferenceId;
  let { displayed: displayDifferences, hidden: hiddenDifferences }: { displayed: Difference[]; hidden: Difference[] } =
    yield select(getDisplayedAndHiddenDifferences);
  if (forceVisibleDifferenceId) {
    const forcedVisibleDifference = hiddenDifferences.find((diff: Difference) => diff.id === forceVisibleDifferenceId);
    if (forcedVisibleDifference) {
      displayDifferences = [...displayDifferences, forcedVisibleDifference];
      hiddenDifferences = hiddenDifferences.filter((diff: Difference) => diff.id !== forceVisibleDifferenceId);
    }
  }
  const {
    displayed: displayedRepeats,
    hidden: hiddenRepeats,
  }: { displayed: CtfMatchResultWithDifferences[]; hidden: CtfMatchResultWithDifferences[] } =
    yield select(getDisplayedAndHiddenRepeats);
  // check if the current selected difference is about to be filtered.
  const currentDifferenceId: string = yield select(getFocusedDifferenceId);
  if (
    currentDifferenceId !== forceVisibleDifferenceId &&
    hiddenDifferences.find((diff: Difference) => diff.id === currentDifferenceId)
  ) {
    yield put(inspection.actions.unfocusDifference());
  }

  // check if the current selected repeat is about to be filtered.
  const focusedMatchIndex: number = yield select(getFocusedMatchIndex);
  if (hiddenRepeats.find((repeat) => repeat.index === focusedMatchIndex)) {
    const annotation = PDFAnnotationManager.getInstance(DocumentTypes.target)?.getAnnotationById(
      `repeat-${focusedMatchIndex}`,
    );
    if (annotation) {
      PDFAnnotationManager.repeatAnnotationDeselected(annotation);
    }
  }

  const isNotPrep = isResultUrl() || isReportsUrl(); // todo: maybe this makes no sense to also be passed to drawMarkups
  if (isNotPrep) {
    PDFTronAnnotationManager.changeDocumentsAnnotationVisibility(displayDifferences, displayedRepeats);
    PDFTronAnnotationManager.changeDocumentsAnnotationVisibility(hiddenDifferences, hiddenRepeats, false);
  }

  // Re-center the focused difference after switching views
  const focusedDifference: Difference | undefined = yield select(getFocusedDifference);
  if (focusedDifference) {
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (docManager) {
      // Re-enabling a filter re-adds the type's difference boxes on top of the focused
      // difference's high-contrast overlay (drawDifferences brings boxes to front). Rebuild
      // the overlay so the focused difference stays visually on top of any re-shown
      // differences that share its space.
      docManager.source?.removeHighContrasts();
      docManager.target?.removeHighContrasts();
      PDFTronAnnotationManager.redrawSelectedDifferenceOverlay();

      // Wait for the viewport to resize before recalculating zoom/scroll
      // Use double RAF to ensure layout has completed after React re-renders
      yield call(
        () =>
          new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(resolve);
            });
          }),
      );

      docManager.zoomToDifference(focusedDifference);
      docManager.scrollToDifferences(focusedDifference);

      // Trigger zoom state update to refresh the displayed zoom level in the UI
      useZoomStateStore.getState().setZoomChanging();
    }
  }
}

function* handleStartInspection() {
  const inspectionId: string = yield select(getInspectionId);
  const autosaving: boolean = yield select(getAutosaving);
  const noLiveText: boolean = yield select(getLiveTextNotFound);

  if (autosaving) {
    // Wait for all in-flight updateInspection mutations to finish before starting
    // We wait for at least one success and also poll autosaving flag until false
    yield take(success(updateInspection.toString()));
    // In case multiple updateInspection are in-flight (e.g., settings + input updates),
    // wait until autosaving becomes false before proceeding
    while (true) {
      const stillAutosaving: boolean = yield select(getAutosaving);
      if (!stillAutosaving) break;
      yield take(success(updateInspection.toString()));
    }
  }

  yield put(
    runInspection(inspectionId, {
      noLiveText,
    }),
  );
}

function* handleStartAfterUpdatingAnnotations() {
  // Wait until any current updates finish
  let autosaving: boolean = yield select(getAutosaving);
  if (autosaving) {
    yield take(success(updateInspection.toString()));
    while (true) {
      autosaving = yield select(getAutosaving);
      if (!autosaving) break;
      yield take(success(updateInspection.toString()));
    }
  }

  // Replicate startInspectionFlow side effects and dispatches
  const inspectionId: string = yield select(getInspectionId);
  if (!inspectionId) return;

  yield put(resetRequests([fetchDifferences.toString()]));
  const settings: InspectionSettings = yield select(getInspectionSettings);
  yield put(
    updateInspection(inspectionId, {
      settings: { ...settings, resyncCount: 1, cumulativeNetResyncedDifferences: 0 },
    }),
  );
  // Wait for settings update to persist
  yield take(success(updateInspection.toString()));

  PDFManagerFactory.getPDFDocManager()?.cleanDocumentHighlight();

  if (settings.colorCheck) {
    const colorPointCount =
      PDFManagerFactory.getPDFDocManager()
        ?.getInstance(DocumentTypes.target)
        ?.annotationManager?.getAnnotationsList()
        ?.filter((annot: any) => annot.getCustomData('colorPickerAnnotation') === 'true')?.length ?? 0;
    const isSingleFile: boolean = yield select(getIsSingleFile);

    yield trackTrackerEvent({
      name: 'color-inspection-run',
      data: {
        inspectionMode: isSingleFile ? 'proofread' : 'compare',
        colorPointCount,
      },
    });
  }

  useDocumentToastStateStore.getState().expireAllToast();
  PDFTronManager.setToolMode(PDFTRON_DEFAULT_TOOL);
  yield put(app.actions.setStartingInspection(true));
  yield put(startInspection());
  yield put(inspection.actions.setOpenPanel(false));
  yield put(inspection.actions.selectingZone(false));
  yield put(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
  yield put(app.actions.clearDialogsFromState());
}

// function* handleCancelInspection() {
//   const id = yield select(getInspectionId);
//   // For cancelling we will just put the status back to idle
//   // (the backend will ignore the inspection results if the inspection is set as Pending)

//   yield put(updateInspection(id, { status: InspectionStatuses.idle }));
//   yield put(app.actions.setStartingInspection(false));
// }
function* handleSingleFileInspection() {
  const filesId: FilesIdData = yield select(getFilesIds);
  const inspectionId: string = yield select(getInspectionId);

  if (filesId.masterFileId) {
    yield put(files.actions.transferSourceToTarget());
  }
  if (!inspectionId && (filesId.sampleFileId || filesId.masterFileId)) {
    const createInspectionData: CreateInspectionData = yield select(getCreateInspectionData);
    if (createInspectionData.settings?.singleFile && createInspectionData.masterFileId) {
      createInspectionData.sampleFileId = createInspectionData.masterFileId;
    }
    yield put(createInspection(createInspectionData));
  } else if (inspectionId) {
    const updateInspectionData: UpdateInspectionData = yield select(getUpdateInspectionData);
    if (updateInspectionData.settings?.singleFile && updateInspectionData.masterFileId) {
      updateInspectionData.sampleFileId = updateInspectionData.masterFileId;
    }
    yield put(updateInspectionFiles(inspectionId, updateInspectionData));
  }
}

function* handleGraphicModeChange() {
  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;
  FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);
  if (!getGraphicMode(store.getState())) return;

  const focusedDifference: Difference = yield select(getFocusedDifference);
  const focusedMatchIndex: number = yield select(getFocusedMatchIndex);

  if (focusedDifference && focusedDifference.type === DifferenceTypeNames.Graphics) {
    docManager.GraphicDifferenceFocused([focusedDifference]);
    return;
  }
  if (focusedDifference && focusedDifference.type === DifferenceTypeNames.Markup) {
    docManager.animateMarkup(focusedDifference);
    return;
  }
  if (focusedMatchIndex !== -1) {
    const lastJobMatches: CtfMatchResult[] = yield select(getLastJobMatches);
    const match = lastJobMatches.find((match: CtfMatchResult) => match.index === focusedMatchIndex);
    docManager.GraphicMatchFocused(match as CtfMatchResult);
  }
}

function* handleTransparencyOpacityChange() {
  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  const graphicMode = getGraphicMode(store.getState());
  // Only update if we're in transparency overlay mode
  if (graphicMode !== 'transparentOverlay') return;

  // Update all GraphicAnnotationOverlay and MarkupAnnotationAnimated annotations
  const updateAnnotations = (wrapper: WebViewerWrapper | null) => {
    if (!wrapper) return;
    const annotationManager = wrapper.annotationManager;
    const annotations = annotationManager.getAnnotationsList();

    // Find all transparency overlay annotations
    const overlayAnnotations = annotations.filter(
      (annot) => annot.Subject === 'GraphicAnnotationOverlay' || annot.Subject === 'MarkupAnnotationAnimated',
    );

    // Trigger redraw by updating each annotation
    overlayAnnotations.forEach((annot) => {
      annotationManager.updateAnnotation(annot);
    });
  };

  // Update annotations in both source and target viewers
  updateAnnotations(docManager.source);
  updateAnnotations(docManager.target);
}

// Helper function to convert rect [x, y, width, height] to bounding box {left, top, right, bottom}
function rectToBoundingBox(rect: number[]) {
  return {
    left: rect[0],
    top: rect[1],
    right: rect[0] + rect[2],
    bottom: rect[1] + rect[3],
  };
}

function* handleFocusMatch() {
  const focusedMatchIndex: number = yield select(getFocusedMatchIndex);
  const lastJobMatches: Match[] = yield select(getLastJobMatches);
  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  if (focusedMatchIndex === -1) {
    FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);
    return;
  }

  const match = lastJobMatches.find((match) => match.index === focusedMatchIndex);
  const focusedDifference: Difference = yield select(getFocusedDifference);

  const isUserLinkedEmbeddedImage =
    focusedDifference?.type === DifferenceTypeNames.EmbeddedImage &&
    focusedDifference?.embeddedImageDetails?.userLinkedMatch;

  if (isUserLinkedEmbeddedImage) {
    const matchDetail = focusedDifference.embeddedImageDetails?.matchDetails?.[0];
    if (!matchDetail) {
      FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);
      return;
    }
    const syntheticMatch = {
      index: focusedMatchIndex,
      inputMasterLocation: matchDetail.masterLocation,
      matchDetails: [
        {
          ...matchDetail,
          sampleLocation: {
            ...matchDetail.sampleLocation,
            rotationIndex: 0,
          },
        },
      ],
    };
    docManager.GraphicMatchFocused(syntheticMatch);
  } else if (match) {
    docManager.GraphicMatchFocused(match);
  } else {
    // Handle merged graphics differences that don't have match data
    if (
      focusedDifference &&
      focusedDifference.originalDifferenceIds &&
      focusedDifference.originalDifferenceIds.length > 0 &&
      focusedDifference.matchIndex === focusedMatchIndex
    ) {
      const syntheticMatch = {
        index: focusedMatchIndex,
        inputMasterLocation: {
          pageNumber: focusedDifference.source.location.pageNumber,
          boundingBox: rectToBoundingBox(focusedDifference.source.location.rect),
        },
        matchDetails: [
          {
            confidence: 1.0,
            masterLocation: {
              pageNumber: focusedDifference.source.location.pageNumber,
              boundingBox: rectToBoundingBox(focusedDifference.source.location.rect),
            },
            sampleLocation: {
              pageNumber: focusedDifference.target.location.pageNumber,
              boundingBox: rectToBoundingBox(focusedDifference.target.location.rect),
            },
          },
        ],
      };
      docManager.GraphicMatchFocused(syntheticMatch);
    }
  }
}

function* handleDifferenceBoxVisibility() {
  PDFTronAnnotationManager.redrawDifferenceBoxes();
}

function* handleRepeatVisibility() {
  const {
    displayed: displayDifferences,
    hidden: hiddenDifferences,
  }: { displayed: Difference[]; hidden: Difference[] } = yield select(getDisplayedAndHiddenDifferences);
  const {
    displayed: displayedRepeats,
    hidden: hiddenRepeats,
  }: { displayed: CtfMatchResultWithDifferences[]; hidden: CtfMatchResultWithDifferences[] } =
    yield select(getDisplayedAndHiddenRepeats);

  const isNotPrep = isResultUrl() || isReportsUrl();
  if (isNotPrep) {
    PDFTronAnnotationManager.changeDocumentsAnnotationVisibility(displayDifferences, displayedRepeats);
    PDFTronAnnotationManager.changeDocumentsAnnotationVisibility(hiddenDifferences, hiddenRepeats, false);
  }
}

function* handleOverlayVisibility() {
  const focusedDifference: Difference = yield select(getFocusedDifference);
  if (!focusedDifference) return;

  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  docManager?.source?.handleOverlayVisibilityChange();
  docManager?.target?.handleOverlayVisibilityChange();
  PDFTronAnnotationManager.redrawSelectedDifferenceOverlay();
}

function* handleFocusDifference(): Generator<any, void, any> {
  const docManager = PDFManagerFactory.getPDFDocManager();

  const focusedDifference: Difference | undefined = yield select(getFocusedDifference);

  // Check if focused difference exists
  if (!focusedDifference) {
    return;
  }

  const { type, id: differenceId, groupId: focusedGroupId } = focusedDifference;

  // When a markup is focused — e.g. immediately after it's drawn — switch to the dedicated Markups tab
  // so the focused markup is visible. The All tab already shows markups, so leave it untouched.
  const selectedModule: ReturnType<typeof getSelectedModule> = yield select(getSelectedModule);
  if (type === DifferenceTypeNames.Markup) {
    yield put(app.actions.setShowMarkupTab(true));
    if (selectedModule !== CTFModuleTypes.markup && selectedModule !== 'All') {
      const sortBy: SortDifferencesOption | undefined = yield select(getStoredDifferenceSortOption);
      yield put(app.actions.setSelectedModule(CTFModuleTypes.markup));
      yield put(app.actions.setHideDisplayPanels(getHideDisplayPanelsForModule(CTFModuleTypes.markup)));
      const shouldResetSort =
        sortBy === SortDifferencesOption.differenceType || sortBy === SortDifferencesOption.fontSize;
      yield put(
        inspection.actions.setDifferenceViewOptions({
          forceVisibleDifferenceId: differenceId,
          ...(shouldResetSort ? { sortBy: SortDifferencesOption.readingOrder } : {}),
        }),
      );
    }
  }

  // Begin 3s zoom-after-selection window (store-based)
  useDiffZoomStore.getState().markSelection(differenceId);
  const currentGroupedDifferences: Difference[] = yield select(getDifferenceGroupMembers, focusedDifference);

  const previousGroupedDifferences: ReturnType<ReturnType<typeof getGroupedDifferences>> = yield select(
    getGroupedDifferences(yield select(getPreviousDifferenceId)),
  );
  const currentGroupedDifferencesType = currentGroupedDifferences[0].type;
  const previousGroupedDifferencesType = previousGroupedDifferences[0]?.type;
  const currentGroup: false | Group = yield select(getGroupById(focusedGroupId));
  const inspectionId: string = yield select(getInspectionId);
  const snackMessage: GVSnackMessage = yield select(getSnackMessage);

  if (
    (type !== DifferenceTypeNames.Deletion ||
      type !== (DifferenceTypeNames.UnmatchedAnnotation as DifferenceTypeNames)) &&
    snackMessage &&
    snackMessage.type === 'info'
  ) {
    yield put(app.actions.clearSnackMessageType('info'));
  }

  // if Its a group we need to patch the group to set viewed = true, and if its a difference, then patch the difference instead.
  if (currentGroup) {
    if (currentGroup.viewed === false) {
      yield put(patchGroup(inspectionId, currentGroup.id, { viewed: true }));
    }
  } else if (currentGroupedDifferences[0].viewed === false) {
    yield put(updateDifference(currentGroupedDifferences[0].id, { viewed: true }));
  }

  // Track selection
  yield trackTrackerEvent({
    name: 'difference-selected',
    data: {
      differenceId,
      differenceType: focusedDifference.type,
      groupType: currentGroup ? 'grouped' : 'single',
      inspectionId,
    },
  });

  if (!docManager) return;

  // Reset markup comparison mode if active to prevent faulty state when selecting different differences
  if (
    focusedDifference.type !== DifferenceTypeNames.Markup ||
    (docManager?.markupMode === 'comparison' && focusedDifference.id !== docManager.originalMarkupId)
  ) {
    docManager.resetMarkupMode();
  }

  const focusedMatchIndex: number = yield select(getFocusedMatchIndex);

  // Clear focusedMatchIndex when focusing a non-graphics difference
  // Graphics/EmbeddedImage differences will set their own matchIndex in GraphicDifferenceFocused
  // This prevents stale match indices from triggering animations on non-graphic differences
  if (focusedMatchIndex !== -1 && type !== DifferenceTypeNames.Graphics && type !== DifferenceTypeNames.EmbeddedImage) {
    yield put(app.actions.setFocusedMatchIndex(-1));
  }

  // remove overlays from other pages before applying a new overlay
  if (previousGroupedDifferences.length || focusedMatchIndex !== -1) {
    docManager?.source?.removeHighContrasts();
    docManager?.target?.removeHighContrasts();
  }

  // Defensive sync: ensure user-linked embedded image annotations are on the correct
  // page before the focus pipeline reads their bounds.  drawDifferences may not have
  // fully re-registered hidden placeholders that moved across pages.
  if (
    focusedDifference.type === DifferenceTypeNames.EmbeddedImage &&
    focusedDifference.embeddedImageDetails?.userLinkedMatch
  ) {
    [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
      const wrapper = docManager.getInstance(docType);
      if (!wrapper) return;
      const annot = wrapper.getAnnotationById(differenceId);
      if (!annot) return;
      const loc = focusedDifference[docType]?.location;
      if (!loc?.pageNumber) return;

      if (annot.PageNumber !== loc.pageNumber) {
        const am = wrapper.instance.Core.annotationManager;
        am.deleteAnnotation(annot, { imported: true });

        const rect = loc.rect || [0, 0, 0, 0];
        const growth = Utils.annotationOptions.unselectedRectangeGrowthValue;
        const norm = normalizeAnnotationRect(rect, growth);
        annot.setX(norm.x);
        annot.setY(norm.y);
        annot.setWidth(norm.w);
        annot.setHeight(norm.h);
        annot.setPageNumber(loc.pageNumber);
        annot.Hidden = false;
        annot.NoView = false;

        am.addAnnotation(annot, { imported: true });
        am.redrawAnnotation(annot);
      }
    });
  }

  // Hide every other difference box before the page is painted, so the redraw below does not flash them.
  const hideOtherDifferences: boolean = yield select(getHideOtherDifferences);
  if (hideOtherDifferences) {
    PDFTronAnnotationManager.syncHiddenOtherDifferenceBoxes();
  }

  // Explicitly draw the overlay annotations to ensure they're painted immediately
  const sourcePageNumber = focusedDifference.source?.location?.pageNumber;
  const targetPageNumber = focusedDifference.target?.location?.pageNumber;

  // Create the high-contrast background, scoped to the focused member's page plus whatever else is
  // already on screen. Pages that scroll in later are drawn on demand (visiblePagesChanged ->
  // docManager.drawVisibleGroupHighContrast), so a group spanning many pages no longer refreshes
  // every page up front. Scoped per side so source/target routing (e.g. Color, Deletion) is preserved.
  //
  // The currently-visible pages have to be included here: visiblePagesChanged is edge-triggered, so
  // focusing a difference on the page you're already viewing never fires it, and the group's other
  // on-screen pages would stay blank after removeHighContrasts wiped them.
  const sourceVisiblePages =
    docManager.source?.docViewer.getDisplayModeManager().getDisplayMode().getVisiblePages() ?? [];
  const targetVisiblePages =
    docManager.target?.docViewer.getDisplayModeManager().getDisplayMode().getVisiblePages() ?? [];

  if (sourcePageNumber) {
    docManager?.applyHighContrast(currentGroupedDifferences, {
      pages: [sourcePageNumber, ...sourceVisiblePages],
      documentType: DocumentTypes.source,
    });
  }
  if (targetPageNumber) {
    docManager?.applyHighContrast(currentGroupedDifferences, {
      pages: [targetPageNumber, ...targetVisiblePages],
      documentType: DocumentTypes.target,
    });
  }

  if (sourcePageNumber && docManager.source) {
    // if current page is different from the difference page, set the current page to the difference page
    if (docManager.source?.docViewer.getCurrentPage() !== sourcePageNumber) {
      docManager.source?.docViewer.setCurrentPage(sourcePageNumber, false);
    }
    yield call(() => docManager.source?.annotationManager.drawAnnotations({ pageNumber: sourcePageNumber }));
  }
  if (targetPageNumber && docManager.target) {
    if (docManager.target?.docViewer.getCurrentPage() !== targetPageNumber) {
      docManager.target?.docViewer.setCurrentPage(targetPageNumber, false);
    }
    yield call(() => docManager.target?.annotationManager.drawAnnotations({ pageNumber: targetPageNumber }));
  }

  // Reconcile markers when focusing a deletion/insertion, or when leaving one so its markers get hidden again.
  if (
    currentGroupedDifferencesType === DifferenceTypeNames.Deletion ||
    currentGroupedDifferencesType === DifferenceTypeNames.Insertion ||
    previousGroupedDifferencesType === DifferenceTypeNames.Deletion ||
    previousGroupedDifferencesType === DifferenceTypeNames.Insertion
  ) {
    docManager?.manageDeletionMarkerVisibility(currentGroupedDifferences);
  }

  // remove flash animation if it exists
  FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);

  if (
    currentGroupedDifferencesType === DifferenceTypeNames.Graphics ||
    currentGroupedDifferencesType === DifferenceTypeNames.EmbeddedImage
  ) {
    docManager.GraphicDifferenceFocused(currentGroupedDifferences);
  } else if (currentGroupedDifferencesType === DifferenceTypeNames.Color) {
    docManager.colorDifferenceFocused(currentGroupedDifferences);
  } else if (currentGroupedDifferencesType === DifferenceTypeNames.Markup) {
    docManager.animateMarkup(currentGroupedDifferences[0]);
  }

  // if live text is enabled when the difference is selected, we must redraw the text highlight after the high contrast annotation
  // for graphics differences this is handled in GraphicZone.animateGraphicDifference()
  const showLiveText: boolean = yield select(getShowLiveText);
  if (showLiveText && currentGroupedDifferencesType !== DifferenceTypeNames.Graphics) {
    PDFTronManager.redrawLiveTextHighlights();
  }

  // if selected tool is resync, reset tool to default
  const selectedTool: PDFTool = yield select(getSelectedTool);
  if (selectedTool === PDFTronTools.RESYNC) {
    yield put(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
  }

  // Zoom and scroll to the difference after all overlays are drawn
  // Small timeout to ensure any final rendering completes
  //
  // Keep zoom and scroll adjacent, last, and deferred. centerOnAnnotation maps page coordinates to
  // window coordinates against the zoom that was just applied, and other focus handlers above
  // (colorDifferenceFocused, GraphicDifferenceFocused -> GraphicMatchFocused) move the viewport
  // themselves — so this pair has to run last to win. Splitting it (zoom early, scroll late) lands
  // the viewer at the wrong location for those difference types.
  yield call(
    () =>
      new Promise((resolve) => {
        setTimeout(() => {
          // Zoom first to ensure correct rendering, then scroll to center
          docManager.zoomToDifference(focusedDifference);
          useZoomStateStore.getState().setZoomChanging();
          docManager.scrollToDifferences(focusedDifference);
          resolve(undefined);
        }, 50);
      }),
  );
}

function* handleDeselectDifference() {
  const groupedDifferences: Difference[] = yield select(getGroupedDifferences(yield select(getPreviousDifferenceId)));
  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  // Deselect difference
  docManager.deselectDifference(groupedDifferences);
  docManager.target?.removeHighContrasts();
  docManager.source?.removeHighContrasts();
  docManager.source?.instance.UI.annotationPopup.update([]);
  docManager.target?.instance.UI.annotationPopup.update([]);
  useZoomStateStore.getState().setZoomChanging();
  docManager.hideAllDeletionMarkers();

  // Stop any active animations
  FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);

  const hideOtherDifferences: boolean = yield select(getHideOtherDifferences);
  if (hideOtherDifferences) {
    PDFTronAnnotationManager.syncHiddenOtherDifferenceBoxes();
  }
}

function* handleClearGroupedDifferencesCache() {
  clearGroupedDifferencesSelectorCache();
}

function* handleResetInspection() {
  yield put(files.actions.resetStore());
  yield put(inspection.actions.resetStore());
  yield put(app.actions.resetStore());
  yield put(
    files.actions.setActiveFileTransferId({
      documentType: DocumentTypes.source,
      id: null,
    }),
  );
  yield put(
    files.actions.setActiveFileTransferId({
      documentType: DocumentTypes.target,
      id: null,
    }),
  );
  yield put(report.actions.resetStore());
  yield put(
    resetRequests([
      fetchInspection.toString(),
      fetchDifferences.toString(),
      fetchGroups.toString(),
      'request/fetchFile',
    ]),
  );
  // Drop the previous session's numbered list so it can't leak into the next inspection.
  useDifferenceListStore.getState().clear();
}

function* handleAddFiles(action: any): Generator<any, void, any> {
  if (!action.payload.isNewFile) {
    // if we are fetching a file that is already part of the inspection, we dont need to update inspection data
    return;
  }

  const creatingInspection: boolean = yield select(getUpdatingInspection);
  // if the inspection is being created we wait until it finishes
  if (creatingInspection) {
    yield take(inspection.actions.setInspectionData.type);
  }

  yield put(app.actions.setUpdatingInspection(true));

  const id: string = yield select(getInspectionId);
  if (id) {
    const updateInspectionData: UpdateInspectionData = yield select(getUpdateInspectionData);
    yield put(updateInspectionFiles(id, updateInspectionData));
  } else {
    const isSingleFile = yield select(getIsSingleFile);
    const filesId = yield select(getFilesIds);
    const filesLoaded = isSingleFile || (!isSingleFile && filesId.masterFileId && filesId.sampleFileId);
    if (filesLoaded) {
      const createInspectiondata: CreateInspectionData = yield select(getCreateInspectionData);
      yield put(createInspection(createInspectiondata));
    } else {
      yield put(app.actions.setUpdatingInspection(false));
    }
  }
}

function* handleResetDocumentState() {
  const creatingInspection: boolean = yield select(getUpdatingInspection);
  // if the inspection is being created we wait until it finishes
  if (creatingInspection) {
    yield take(inspection.actions.setInspectionData.type);
  }

  const id: string = yield select(getInspectionId);
  if (id) {
    yield put(report.actions.resetStore());

    yield put(app.actions.setUpdatingInspection(true));
    const updateInspectionData: UpdateInspectionData = yield select(getUpdateInspectionData);
    yield put(updateInspectionFiles(id, updateInspectionData));
  }
}

function* handleResyncCancelled(action: PayloadAction<boolean>) {
  // Only clean up when resync is actually cancelled (true), not when it's reset to false
  if (action.payload === true) {
    cleanupResyncZonesAndResetTool();
  }
}

function* handleSelectUnconfirmedZones() {
  const sourceUnconfirmedZones = PDFManagerFactory.getPDFDocManager()?.getUnconfirmedZones();
  if (sourceUnconfirmedZones && sourceUnconfirmedZones.length > 0) {
    PDFManagerFactory.getPDFDocManager()?.source?.annotationManager.selectAnnotation(sourceUnconfirmedZones[0]);
  }
}

const addHoverEventListenersToSourceAndTarget = () => {
  const sourceWebViewer = PDFManagerFactory.getViewer(DocumentTypes.source);
  const targetWebViewer = PDFManagerFactory.getViewer(DocumentTypes.target);
  const iframeWindows = [];
  iframeWindows.push(sourceWebViewer?.getIframeWindow());
  iframeWindows.push(targetWebViewer?.getIframeWindow());
  iframeWindows.forEach((iframeWindow) => {
    iframeWindow?.addEventListener('mouseover', () => {
      iframeWindow.focus();
    });
  });
};

function* handleAutoShapeJobs(
  isSingleFile: boolean,
  filesMetadata: { masterMetadata: FileMetadata; sampleMetadata: FileMetadata },
) {
  // Helper function to apply auto-selected shapes from preprocessor
  const applyAutoSelectedShapes = function* (
    documentType: DocumentTypes,
    metadata: FileMetadata,
  ): Generator<any, void, any> {
    const selectedLayerName = metadata?.selectedLayerName;
    const selectedSeparationName = metadata?.selectedSeparationName;
    const autoSelectedShapes = metadata?.autoSelectedShapes;
    const recommendedBoxType = metadata?.recommendedBoxType;

    if (recommendedBoxType) {
      const docManager = PDFManagerFactory.getPDFDocManager();
      if (docManager) {
        yield call([docManager, docManager.applyPDFBox], documentType, recommendedBoxType.regions);
      }
    } else if (selectedLayerName && autoSelectedShapes && autoSelectedShapes.length > 0) {
      // Get layers from store to find the correct index
      const layers = yield select(documentType === DocumentTypes.source ? getSourceLayers : getTargetLayers);
      const layerIndex = layers?.findIndex((layer: Layer) => layer.name === selectedLayerName);

      if (layerIndex !== undefined && layerIndex !== -1) {
        // Generate key matching the manual UI flow format: `${itemType}-${index}-${name}`
        const key = `Layer-${layerIndex}-${selectedLayerName}`;

        // Set polygon state with the auto-selected shapes from preprocessor
        yield put(
          inspection.actions.setPolygons({
            documentType,
            points: autoSelectedShapes,
            type: 'Layer',
            key,
            layerOrSeparationName: selectedLayerName,
          }),
        );
      }
    } else if (selectedSeparationName && autoSelectedShapes && autoSelectedShapes.length > 0) {
      // Get separations from store to find the correct index
      const separations = yield select(
        documentType === DocumentTypes.source ? getSourceSeparations : getTargetSeparations,
      );
      const separationIndex = separations?.findIndex(
        (separation: Separation) => separation.name === selectedSeparationName,
      );

      if (separationIndex !== undefined && separationIndex !== -1) {
        // Generate key matching the manual UI flow format: `${itemType}-${index}-${name}`
        const key = `Separation-${separationIndex}-${selectedSeparationName}`;

        // Set polygon state with the auto-selected shapes from preprocessor
        yield put(
          inspection.actions.setPolygons({
            documentType,
            points: autoSelectedShapes,
            type: 'Separation',
            key,
            layerOrSeparationName: selectedSeparationName,
          }),
        );
      }
    }
  };

  // Apply auto-selected shapes from preprocessor if available
  if (!isSingleFile) {
    // In comparison mode, check source first, then target
    yield call(applyAutoSelectedShapes, DocumentTypes.source, filesMetadata.masterMetadata);
    yield call(applyAutoSelectedShapes, DocumentTypes.target, filesMetadata.sampleMetadata);
  } else {
    // In proofreader mode, only check target
    yield call(applyAutoSelectedShapes, DocumentTypes.target, filesMetadata.sampleMetadata);
  }
}

function* handleLoadingState() {
  const hasloaded: boolean = yield select(getDocumentsLoaded);
  if (hasloaded) {
    addHoverEventListenersToSourceAndTarget();
    // update whether the dimensions of files are equal for graphic zones
    PDFManagerFactory.getPDFDocManager()?.updateAreFileDimensionsEqual();

    const uploadedFile: { source: InspectFileState; target: InspectFileState } = yield select(getUploadedFilesSelector);
    const sourceFileName = uploadedFile.source.originalName;
    const newFileName = uploadedFile.target.originalName;
    const differentFileType = tableEnhancementFileCheck(sourceFileName, newFileName);
    yield put(inspection.actions.setTableEnhancementEnabledState(differentFileType));

    // Only process auto-select layers/separations if we have metadata from auto-select processing
    const hasAutoSelectMetadata: boolean = yield select(fileSelectors.hasAutoSelectLayersSeparationsMetadata);

    if (hasAutoSelectMetadata) {
      const isSingleFile: boolean = yield select(getIsSingleFile);
      const filesMetadata: { masterMetadata: FileMetadata; sampleMetadata: FileMetadata } = yield select(
        fileSelectors.getFilesMetadata,
      );
      yield call(handleAutoShapeJobs, isSingleFile, filesMetadata);
    }
  } else {
    yield put(inspection.actions.setTableEnhancementEnabledState(false));
  }
}

function* handleShowPanToolToast() {
  const toastStatuses: ToastStatuses = yield select(getToastStatuses);
  const userPrefs: UserPreferences = yield select(getUserPreferences) as UserPreferences;

  if (!toastStatuses.hasPanToolToastShown && !userPrefs?.disablePanToolToast) {
    useDocumentToastStateStore.getState().setDocumentToast(DocumentTypes.source, toastEvents.panToolToast);
    yield put(app.actions.panToolToastHasShown());
  }
}

function* handleNewPolygonPoints(
  action: PayloadAction<{
    documentType: DocumentTypes;
    points: [number, number][][][] | '';
    callSaveInspection?: boolean;
  }>,
) {
  const inspectionId: string = yield select(getInspectionId);
  const { documentType, points, callSaveInspection = true } = action.payload;

  if (typeof points === 'string') {
    const annotationManager = PDFTronAnnotationManager.getInstance(documentType);
    if (!annotationManager) return;
    annotationManager.deleteAnnotations(
      annotationManager
        .getAnnotationsList()
        .filter(
          (annot: Core.Annotations.Annotation) =>
            annot.Subject === 'Polygon' && annot.getCustomData(PolygonZoneCustomData.shapePolygon) === 'true',
        ),
      { imported: false, force: true },
    );
  } else {
    const manager = PDFManagerFactory.getPDFDocManager();
    if (!manager) return;
    if (!isResultUrl()) {
      manager.drawPolygons(documentType, points);
    }
  }

  if (callSaveInspection && inspectionId) {
    yield call(saveInspection);
  }
}

function* handleGrayscaleMode() {
  const grayscaleMode: boolean = yield select(getGrayscaleMode);
  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  applyGrayscaleMode(docManager.source?.docViewer, grayscaleMode);
  applyGrayscaleMode(docManager.target?.docViewer, grayscaleMode);
}

function* handleFileUploadStart({ payload, meta }: FileUploadRequest) {
  const requestData = payload?.request?.data;
  // Generate a session ID to correlate file upload and download events for timing analysis.
  // This helps distinguish initial file processing from subsequent page refreshes:
  // - On upload: Generate new session ID
  // - On download: Only track timing if it has a matching session ID
  // - On page refresh: Session ID is cleared, so refreshed downloads are ignored
  const fileTransferId = uuidv4();

  yield put(
    files.actions.setActiveFileTransferId({
      documentType: meta?.document,
      id: fileTransferId,
    }),
  );

  const fileUploadStartEvent = {
    name: 'file-uploaded',
    data: {
      fileTransferId,
      fileName: requestData?.fileName,
      type: meta?.document,
      'remove-header-footer': requestData?.removeHeadersFooters ? 'True' : 'False',
    },
  };
  yield trackTrackerEvent(fileUploadStartEvent);
}

function* handleFileDownloadSuccess({ payload, meta }: FetchFilePayload) {
  const fileTransferId: string | null = yield select((state) =>
    fileSelectors.getActiveFileTransferId(state, meta?.document),
  );

  const fileDownloadEvent = {
    name: 'file-downloaded',
    data: {
      fileName: payload?.data?.filename,
      type: meta?.document,
      ...(fileTransferId && { fileTransferId }),
    },
  };
  yield trackTrackerEvent(fileDownloadEvent);
}

function* handleBatchFileUploadStart({ meta }: BatchFileUploadRequest) {
  const fileTransferId = uuidv4();

  yield put(
    files.actions.setActiveFileTransferId({
      documentType: meta?.document,
      id: fileTransferId,
    }),
  );

  const fileUploadStartEvent = {
    name: 'file-uploaded',
    data: {
      fileTransferId,
      fileCount: meta?.fileCount,
      fileName: meta?.fileName,
      type: meta?.document,
      'remove-header-footer': meta?.removeHeadersFooters ? 'True' : 'False',
    },
  };
  yield trackTrackerEvent(fileUploadStartEvent);
}

// New saga to capture and set client timezone offset
function* initializeClientMetadataSaga() {
  try {
    const timezoneOffset = new Date().getTimezoneOffset();
    yield put(setClientTimezoneOffset(timezoneOffset));
  } catch (error) {
    // Handle any potential errors, though unlikely for getTimezoneOffset
    console.error('Error initializing client metadata:', error);
  }
}

function* recomputeDifferenceNumbering() {
  const selectedModule: string = yield select(getSelectedModule);

  // Short-circuit the All tab before reading any other selector. Two reasons:
  // 1. The All tab uses the legacy sort/numbering path in ListOfDifferences — nothing to compute.
  // 2. This watcher fires (via take('*')) on the first action dispatched during store startup,
  //    while the `store` barrel is still initializing inside a circular import graph. 'All' is the
  //    default module then, so returning here avoids reading getInspectionId — whose defining module
  //    (inspectionSelectors) initializes late in that cycle, so touching it now throws a temporal
  //    dead zone error ("Cannot access 'getInspectionId' before initialization").
  // TODO: remove this branch when the showAllResultsTab flag is retired.
  if (selectedModule === 'All') {
    useDifferenceListStore.getState().clear();
    return;
  }

  const subgrouped: SubgroupedDifferences<Difference> = yield select(getSubgroupedDifferences);
  const unfiltered: Difference[] = yield select(getUnfilteredDifferences);
  const sortOption: SortDifferencesOption = yield select(getDifferenceSortOption);
  const inspectionId: string = yield select(getInspectionId);

  // Status titles live in React Query, not Redux, so they're read from the query cache here.
  const statuses = queryClient.getQueryData<InspectionCustomStatuses>(['inspectionCustomStatuses', inspectionId]);

  rebuildDifferenceList(subgrouped, unfiltered, selectedModule, sortOption, statuses);
}

/**
 * Keeps the difference list store in sync by watching the derived data, not individual actions.
 * Any mutation (update, patch, bulk edit, restore, unmerge, merge, resync, etc.) replaces the
 * fetchDifferences query data, which changes the getUnfilteredDifferences reference — so a single
 * reference check catches them all, including optimistic updates and rollbacks. Sort and module
 * changes are picked up the same way. This avoids an action allow-list that silently goes stale
 * whenever a new mutation is added.
 */
function* watchDifferenceListChanges() {
  let prevUnfiltered: unknown;
  let prevSort: unknown;
  let prevModule: unknown;

  while (true) {
    // Wake on every action and let the reference check below decide, rather than listing specific
    // action types (which drifts out of date). The cost is three memoized selector reads per action.
    yield take('*');

    const unfiltered: Difference[] = yield select(getUnfilteredDifferences);
    const sortOption: SortDifferencesOption = yield select(getDifferenceSortOption);
    const selectedModule: string = yield select(getSelectedModule);

    if (unfiltered !== prevUnfiltered || sortOption !== prevSort || selectedModule !== prevModule) {
      prevUnfiltered = unfiltered;
      prevSort = sortOption;
      prevModule = selectedModule;
      yield call(recomputeDifferenceNumbering);
    }
  }
}

export default function* rootSaga() {
  // This actions will trigger
  const autoSaveActions = [
    inspection.actions.setAnnotations.type,
    inspection.actions.setPageRange.type,
    inspection.actions.setZoneSelected.type,
    inspection.actions.setInspectionSettings.type,
    inspection.actions.setSelectedCustomDictionaryIDs.type,
    inspection.actions.setLayers.type,
    inspection.actions.setSeparations.type,
    inspection.actions.setGraphicsOptions.type,
    inspection.actions.setBarcodeSettings.type,
    inspection.actions.setPolygons.type,
    inspection.actions.toggleGraphics.type,
    inspection.actions.deleteOutdatedGraphicZones.type,
    inspection.actions.setOverprint.type,
  ];

  // @todo move to pdftron folder
  yield all([
    call(initializeClientMetadataSaga),
    takeEvery(inspection.actions.excludePage.type, function ({ payload }: ExcludePagePayload) {
      const { pageNumber, documentType } = payload;
      excludePagesFromDocuments([pageNumber], documentType);
    }),
    takeEvery(inspection.actions.excludePages.type, function ({ payload }: ExcludePagesPayload) {
      const { pageNumbers, documentType } = payload;
      excludePagesFromDocuments(pageNumbers, documentType);
    }),
    takeEvery(success(updateUserData.type), function* ({ meta }: any) {
      // self changing names and password are from the same action, but we don't track users' password
      if (meta.userInfo.values.firstName || meta.userInfo.values.lastName) {
        yield trackTrackerEvent({
          name: 'user-self-updated',
          data: {
            firstNameOldValue: meta.userInfo.initialValues.firstName,
            lastNameOldValue: meta.userInfo.initialValues.lastName,
            firstNameNewValue: meta.userInfo.values.firstName,
            lastNameNewValue: meta.userInfo.values.lastName,
          },
        });
      } else if (meta.userInfo.values.currentPassword && meta.userInfo.values.newPassword) {
        yield trackTrackerEvent({
          name: 'user-change-password',
          data: {},
        });
      }
    }),
    takeEvery(success(fetchFile.type), handleFileDownloadSuccess),
    takeEvery(startFileCreation.type, handleFileUploadStart),
    takeEvery('files/startBatchFileCreation', handleBatchFileUploadStart),
    takeEvery(app.actions.traceLoginAttempts.type, function* ({ payload }: PayloadAction<{ username: string }>) {
      yield trackTrackerEvent({
        name: 'user-login-attempted',
        data: { username: payload.username },
      });
    }),
    takeEvery(success(fetchDifferences.toString()), function* ({ meta }: any) {
      // we take from fetchDifferences to track inspection results because we get differences after but they are needed in the track event
      if (!meta?.shouldTrackInspection) return;

      const differences: ReturnType<typeof getDifferences> = yield select(getDifferences);
      const differencesQty = differences.length;
      const differencesType: number = yield select(getSegmentDisplayedDifferenceCounts);
      const smartDiscardQty: number = yield select(getSmartDiscardQty);
      const projectName: string = yield select(getName);
      const uploadedFiles: { source: InspectFileState; target: InspectFileState } =
        yield select(getUploadedFilesSelector);
      const excludedRange: { source: number[]; target: number[] } = yield select(getExcludedPage);
      const annotations: { source: InputAnnotation[]; target: InputAnnotation[] } = yield select(getAnnotations);
      const spellingTrackingData: string = yield select(getSpellingTrackingData);
      const inspectionSettings: InspectionSettings = yield select(getInspectionSettings);
      const { GraphicCreateTool }: ReturnType<typeof getNumberOfAnnotations> = yield select(getNumberOfAnnotations);
      const lastJobMatches: Match[] = yield select(getLastJobMatches);

      yield trackTrackerEvent({
        name: 'document-inspected',
        data: {
          differencesQty,
          differencesType,
          projectName,
          settings: inspectionSettings,
          masterFileId: uploadedFiles?.source?.fileId,
          masterFileName: uploadedFiles?.source?.fileName,
          sampleFileId: uploadedFiles?.target?.fileId,
          sampleFileName: uploadedFiles?.target?.fileName,
          inclusions: [{ excludedRange, annotations }],
          spelling: spellingTrackingData,
          matchedGraphics: GraphicCreateTool.source.matchedGraphic,
          fullPageGraphics: `${inspectionSettings.fullPageGraphics}`,
          shiftedGraphics: GraphicCreateTool.source.shifted,
          scaledGraphics: GraphicCreateTool.source.scaled,
        },
      });
      yield trackTrackerEvent({
        name: 'Differences-per-inspection',
        data: {
          differencesQty,
          smartDiscardQty,
          differencesType,
          repeatsDetected: lastJobMatches.length || null,
        },
      });
    }),
    takeEvery(startInspection.type, handleStartInspection),
    takeEvery(inspection.actions.setLoadingState.type, handleLoadingState),
    // Debounce for 1 second before updating inspections as setAnnotations can
    // occur multiple times in a short amount of time (e.g. excluded pages)
    debounce<string[], () => void>(1000, autoSaveActions, autoSaveInspection),
    takeEvery(success(pollInspectionStatus.toString()), function* () {
      yield put(app.actions.setResetIdleTimer(true));
    }),
    takeEvery(success(fetchFileList.type), handleFilesList),
    // we need this one to set the autosaving flag without the delay of the debounce
    takeEvery([...autoSaveActions], handleSetAutosavingFlag),
    takeEvery(inspection.actions.setName.type, autoSaveName),
    takeEvery(selectUnconfirmedZones.type, handleSelectUnconfirmedZones),
    takeEvery(inspection.actions.focusDifference.type, handleFocusDifference),
    takeEvery(app.actions.setGraphicMode.type, handleGraphicModeChange),
    takeEvery(app.actions.setTransparencyOpacity.type, handleTransparencyOpacityChange),
    takeEvery(app.actions.setShowDifferenceBoxes.type, handleDifferenceBoxVisibility),
    takeEvery(app.actions.setHideOtherDifferences.type, function* () {
      PDFTronAnnotationManager.syncHiddenOtherDifferenceBoxes();
    }),
    takeEvery(app.actions.setShowRepeats.type, handleRepeatVisibility),
    takeEvery(app.actions.setShowOverlay.type, handleOverlayVisibility),
    takeEvery(inspection.actions.unfocusDifference.type, handleDeselectDifference),
    takeEvery(inspection.actions.resetStore.type, handleClearGroupedDifferencesCache),
    takeEvery(resetInspection.type, handleResetInspection),
    takeEvery(files.actions.setFetchedFile.type, handleAddFiles),
    takeEvery(files.actions.setFileFromLibrary.type, handleAddFiles),
    takeEvery(app.actions.setFocusedMatchIndex.type, handleFocusMatch),
    takeEvery(inspection.actions.setDifferenceViewOptions.type, handleSetDifferenceViewOptions),
    takeEvery(app.actions.setSelectedModule.type, handleSetDifferenceViewOptions),
    call(watchDifferenceListChanges),
    takeEvery(inspection.actions.resetDocumentState.type, handleResetDocumentState),
    takeEvery(inspection.actions.setResyncCancelled.type, handleResyncCancelled),
    takeEvery(inspection.actions.startInspectionAfterUpdatingAnnotations.type, handleStartAfterUpdatingAnnotations),
    // Debounce for 1 second before updating inspections as setAnnotations can
    // occur multiple times in a short amount of time (e.g. excluded pages)
    takeEvery(inspection.actions.increaseTextZoneId, handleShowPanToolToast),
    takeEvery(inspection.actions.increaseMarqueeZoneId, handleShowPanToolToast),
    takeEvery(inspection.actions.increaseGraphicZoneId, handleShowPanToolToast),
    takeEvery(inspection.actions.increaseCrossoutZoneId, handleShowPanToolToast),
    takeEvery(inspection.actions.setPolygons.type, handleNewPolygonPoints),
    takeEvery(inspection.actions.setSingleFileInspection.type, handleSingleFileInspection),
    takeEvery(app.actions.setGrayscaleMode.type, handleGrayscaleMode),
    ...reportSagas,
    ...docManagerSagas,
    ...auth0Sagas,
    ...appSagas,
    ...displaySettingsSagas,
    ...differencesSagas,
  ]);
}
